import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Card, PressableCard } from '@/components/ui/card';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AppointmentStatusPill } from './appointment-status-pill';
import { Appointment, APPOINTMENT_STATUS } from '@/types/appointment';
import {
  openAddressInMaps,
  openMeetingUrl,
  resolveAppointmentLocation,
} from '@/utils/appointment-location';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { formatCurrency } from '@/utils/format-currency';

interface Props {
  appointment: Appointment;
  /** Abre o detalhe — é lá que ficam remarcar e cancelar. */
  onOpen?: (a: Appointment) => void;
}

const ACTIONABLE = [APPOINTMENT_STATUS.PENDING, APPOINTMENT_STATUS.CONFIRMED];

export function AppointmentCard({ appointment, onOpen }: Props) {
  const isFuture = new Date(appointment.startAt).getTime() > Date.now();
  const showActions = !!onOpen && isFuture && ACTIONABLE.includes(appointment.status);
  const location = resolveAppointmentLocation(appointment);
  const mapsAddress = location?.address ?? null;
  const meetingUrl = location?.meetingUrl ?? null;

  const body = (
    <View className="gap-3 p-4">
      <View className="flex-row items-start justify-between gap-3">
        <View className="min-w-0 flex-1 gap-1">
          <ThemedText className="font-semibold text-base" numberOfLines={2}>
            {appointment.serviceName ?? 'Serviço'}
          </ThemedText>
          {(!!appointment.providerName || appointment.price != null) && (
            <ThemedText className="text-sm text-neutral-500" numberOfLines={1}>
              {[
                appointment.providerName,
                appointment.price != null ? formatCurrency(appointment.price) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </ThemedText>
          )}
          <View className="flex-row items-center gap-1.5">
            <Ionicons className="text-neutral-500" name="calendar-outline" size={14} />
            <ThemedText className="text-sm text-neutral-500">
              {formatAppointmentWhen(appointment.startAt)}
            </ThemedText>
          </View>
          {!!location && (
            <View className="flex-row items-center gap-1.5">
              <Ionicons className="text-neutral-500" name={location.icon} size={14} />
              <ThemedText className="flex-1 text-sm text-neutral-500" numberOfLines={1}>
                {location.label}
              </ThemedText>
            </View>
          )}
        </View>
        <AppointmentStatusPill status={appointment.status} />
      </View>

      {!!appointment.cancelReason && appointment.status === APPOINTMENT_STATUS.CANCELLED && (
        <View className="rounded-xl bg-neutral-50 px-3 py-2">
          <ThemedText className="text-xs text-neutral-500">
            Motivo: {appointment.cancelReason}
          </ThemedText>
        </View>
      )}

      {showActions && (
        <View className="flex-row gap-2 pt-1">
          <PressableScale
            onPress={() => onOpen(appointment)}
            className="flex-1 flex-row items-center justify-center gap-1.5 rounded-full border border-neutral-200 py-2.5"
          >
            <Ionicons className="text-neutral-600" name="reader-outline" size={16} />
            <ThemedText className="font-semibold text-sm text-neutral-700">Ver detalhes</ThemedText>
          </PressableScale>
          {!!mapsAddress && (
            <PressableScale
              onPress={() => openAddressInMaps(mapsAddress)}
              className="flex-1 flex-row items-center justify-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 py-2.5"
            >
              <Ionicons name="navigate-outline" size={16} color="#FD5001" />
              <ThemedText className="font-semibold text-sm text-primary">Como chegar</ThemedText>
            </PressableScale>
          )}
          {!!meetingUrl && (
            <PressableScale
              onPress={() => openMeetingUrl(meetingUrl)}
              className="flex-1 flex-row items-center justify-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 py-2.5"
            >
              <Ionicons name="videocam-outline" size={16} color="#FD5001" />
              <ThemedText className="font-semibold text-sm text-primary">Entrar na chamada</ThemedText>
            </PressableScale>
          )}
        </View>
      )}
    </View>
  );

  if (!onOpen) return <Card className="w-full">{body}</Card>;

  return (
    <PressableCard
      className="w-full"
      onPress={() => onOpen(appointment)}
      accessibilityLabel={`Ver detalhes de ${appointment.serviceName ?? 'agendamento'}`}
    >
      {body}
    </PressableCard>
  );
}
